import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  IconButton,
  CircularProgress,
  List,
  ListItem,
  useMediaQuery,
  useTheme as useMuiTheme,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { useTheme } from '@/contexts/ThemeContext'
import { useNotification } from '@/hooks/useNotification'
import { createPublicClient, http, type Abi, getAddress } from 'viem'
import { mainnet, sepolia } from 'wagmi/chains'
import { getEnv } from '@/config/env'
import SafeAbi from '@/abis/Safe.json'
import STNGImplementationAbi from '@/abis/STNGImplementation.json'
import { COLORS } from '@/constants/colors'
import { getSafeNonceFromService } from '@/utils/safeTransactionService'
import { copyToClipboard } from '@/utils/clipboard'
import { formatAddress, formatTokenBalance } from '@/utils/formatting'
import {
  MODAL_PAPER_STYLES,
  MODAL_TITLE_STYLES,
  MODAL_CONTENT_STYLES,
  MODAL_BUTTON_STYLES,
} from '@/components/shared/modalStyles'

interface SafeDetailsModalProps {
  open: boolean
  onClose: () => void
  safeAddress: string
  safeName: string
  chainId: string
}

export default function SafeDetailsModal({
  open,
  onClose,
  safeAddress,
  safeName,
  chainId: chainIdStr,
}: SafeDetailsModalProps) {
  const { colors } = useTheme()
  const { showSuccess, showError } = useNotification()
  const muiTheme = useMuiTheme()
  const isDesktop = useMediaQuery(muiTheme.breakpoints.up('md'))

  const [nonce, setNonce] = useState<number | null>(null)
  const [owners, setOwners] = useState<string[]>([])
  const [threshold, setThreshold] = useState<number | null>(null)
  const [stablecoinBalance, setStablecoinBalance] = useState<string | null>(
    null
  )
  const [version, setVersion] = useState<string | null>(null)
  const [guard, setGuard] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const chainId = parseInt(chainIdStr)

  useEffect(() => {
    if (!open) {
      setNonce(null)
      setOwners([])
      setThreshold(null)
      setStablecoinBalance(null)
      setVersion(null)
      setGuard(null)
      setError(null)
      setIsLoading(false)
      return
    }

    if (!safeAddress || !chainId) {
      return
    }

    const fetchSafeDetails = async () => {
      setIsLoading(true)
      setError(null)

      try {
        const { mainnetRpcUrl, sepoliaRpcUrl, stngProxy } = getEnv()

        if (!stngProxy) {
          setError('Stablecoin proxy address not configured')
          setIsLoading(false)
          return
        }

        let rpcUrl: string | undefined
        let chain: typeof mainnet | typeof sepolia

        if (chainId === mainnet.id) {
          rpcUrl = mainnetRpcUrl || mainnet.rpcUrls.default.http[0]
          chain = mainnet
        } else if (chainId === sepolia.id) {
          rpcUrl = sepoliaRpcUrl || sepolia.rpcUrls.default.http[0]
          chain = sepolia
        } else {
          setError(`Unsupported chain ID: ${chainId}`)
          setIsLoading(false)
          return
        }

        if (!rpcUrl) {
          setError('RPC URL not configured for this chain')
          setIsLoading(false)
          return
        }

        const chainPublicClient = createPublicClient({
          chain,
          transport: http(rpcUrl),
        })

        const nonceResult = await getSafeNonceFromService(safeAddress, chainId)
        if (nonceResult.success && nonceResult.nonce !== undefined) {
          setNonce(nonceResult.nonce)
        } else {
          console.warn('Failed to fetch nonce from service:', nonceResult.error)
        }

        const [
          ownersResult,
          thresholdResult,
          versionResult,
          guardResult,
          stablecoinBalanceResult,
          decimalsResult,
          symbolResult,
        ] = await Promise.all([
          chainPublicClient.readContract({
            address: safeAddress as `0x${string}`,
            abi: SafeAbi as Abi,
            functionName: 'getOwners',
          }) as Promise<readonly `0x${string}`[]>,
          chainPublicClient.readContract({
            address: safeAddress as `0x${string}`,
            abi: SafeAbi as Abi,
            functionName: 'getThreshold',
          }) as Promise<bigint>,
          chainPublicClient
            .readContract({
              address: safeAddress as `0x${string}`,
              abi: SafeAbi as Abi,
              functionName: 'VERSION',
            })
            .catch(() => null) as Promise<string | null>,
          chainPublicClient
            .readContract({
              address: safeAddress as `0x${string}`,
              abi: SafeAbi as Abi,
              functionName: 'getGuard',
            })
            .catch(() => null) as Promise<`0x${string}` | null>,
          chainPublicClient
            .readContract({
              address: stngProxy as `0x${string}`,
              abi: STNGImplementationAbi as Abi,
              functionName: 'balanceOf',
              args: [safeAddress as `0x${string}`],
            })
            .catch(() => null) as Promise<bigint | null>,
          chainPublicClient
            .readContract({
              address: stngProxy as `0x${string}`,
              abi: STNGImplementationAbi as Abi,
              functionName: 'decimals',
            })
            .catch(() => null) as Promise<number | null>,
          chainPublicClient
            .readContract({
              address: stngProxy as `0x${string}`,
              abi: STNGImplementationAbi as Abi,
              functionName: 'symbol',
            })
            .catch(() => null) as Promise<string | null>,
        ])

        setOwners(ownersResult.map((addr) => getAddress(addr)))
        setThreshold(Number(thresholdResult))
        if (versionResult) {
          setVersion(versionResult)
        }
        if (
          guardResult &&
          guardResult !== '0x0000000000000000000000000000000000000000'
        ) {
          setGuard(getAddress(guardResult))
        }
        if (
          stablecoinBalanceResult !== null &&
          decimalsResult !== null &&
          symbolResult !== null
        ) {
          const formattedBalance = formatTokenBalance(
            stablecoinBalanceResult,
            decimalsResult,
            symbolResult
          )
          setStablecoinBalance(formattedBalance)
        } else {
          setStablecoinBalance('—')
        }
      } catch (err) {
        console.error('Failed to fetch Safe details:', err)
        setError(
          err instanceof Error ? err.message : 'Failed to fetch Safe details'
        )
      } finally {
        setIsLoading(false)
      }
    }

    fetchSafeDetails()
  }, [open, safeAddress, chainId])

  const handleCopyAddress = async (address: string) => {
    try {
      await copyToClipboard(address)
      showSuccess('Address copied to clipboard')
    } catch (error) {
      console.error('Failed to copy:', error)
      showError('Failed to copy address')
    }
  }

  const dividerStyles = {
    borderColor: colors.primary,
    opacity: 0.3,
    my: 2,
  }

  const detailRowContainerStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    py: 1.5,
    gap: 2,
  }

  const labelColor =
    (colors as typeof COLORS).yellow || (colors as typeof COLORS).primary
  const detailRowLabelStyles = {
    color: labelColor,
    fontFamily: "'TWKEverett', sans-serif",
    fontSize: '0.95rem',
    fontWeight: 600,
    minWidth: '120px',
  }

  const detailRowValueStyles = {
    color: colors.text,
    fontFamily: "'TWKEverett', sans-serif",
    fontSize: '0.95rem',
    wordBreak: 'break-word',
    textAlign: 'right',
    flex: 1,
  }

  const ownerItemStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1,
    py: 1,
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          ...MODAL_PAPER_STYLES,
          backgroundColor:
            (colors as typeof COLORS).background,
        },
      }}
    >
      <DialogTitle
        sx={{
          ...MODAL_TITLE_STYLES,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6" component="span">
          Safe Details: {safeName}
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: colors.text,
            '&:hover': {
              backgroundColor: `${colors.primary}22`,
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={MODAL_CONTENT_STYLES}>
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 4,
              gap: 2,
            }}
          >
            <CircularProgress sx={{ color: colors.primary }} />
            <Typography
              variant="body2"
              sx={{ color: colors.text, opacity: 0.7 }}
            >
              Loading Safe details...
            </Typography>
          </Box>
        ) : error ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 4,
              gap: 2,
            }}
          >
            <Typography
              variant="body1"
              sx={{ color: colors.error, textAlign: 'center' }}
            >
              {error}
            </Typography>
          </Box>
        ) : (
          <Box>
            <Box sx={detailRowContainerStyles}>
              <Typography sx={detailRowLabelStyles}>Address:</Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flex: 1,
                  justifyContent: 'flex-end',
                }}
              >
                <Typography
                  sx={{
                    ...detailRowValueStyles,
                    fontFamily: 'monospace',
                    fontSize: isDesktop ? '0.95rem' : '0.85rem',
                  }}
                >
                  {isDesktop ? safeAddress : formatAddress(safeAddress)}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => handleCopyAddress(safeAddress)}
                  sx={{
                    color: colors.primary,
                    padding: '4px',
                    '&:hover': {
                      backgroundColor: `${colors.primary}20`,
                    },
                  }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            <Divider sx={dividerStyles} />

            <Box sx={detailRowContainerStyles}>
              <Typography sx={detailRowLabelStyles}>Current Nonce:</Typography>
              <Typography sx={detailRowValueStyles}>
                {nonce !== null ? nonce.toString() : '—'}
              </Typography>
            </Box>

            <Divider sx={dividerStyles} />

            <Box sx={detailRowContainerStyles}>
              <Typography sx={detailRowLabelStyles}>Balance:</Typography>
              <Typography sx={detailRowValueStyles}>
                {stablecoinBalance || '—'}
              </Typography>
            </Box>

            <Divider sx={dividerStyles} />

            <Box sx={detailRowContainerStyles}>
              <Typography sx={detailRowLabelStyles}>Threshold:</Typography>
              <Typography sx={detailRowValueStyles}>
                {threshold !== null ? `${threshold} of ${owners.length}` : '—'}
              </Typography>
            </Box>

            <Divider sx={dividerStyles} />

            {version && (
              <>
                <Box sx={detailRowContainerStyles}>
                  <Typography sx={detailRowLabelStyles}>Version:</Typography>
                  <Typography sx={detailRowValueStyles}>{version}</Typography>
                </Box>
                <Divider sx={dividerStyles} />
              </>
            )}

            {guard && (
              <>
                <Box sx={detailRowContainerStyles}>
                  <Typography sx={detailRowLabelStyles}>Guard:</Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      flex: 1,
                      justifyContent: 'flex-end',
                    }}
                  >
                    <Typography
                      sx={{
                        ...detailRowValueStyles,
                        fontFamily: 'monospace',
                        fontSize: '0.85rem',
                      }}
                    >
                      {formatAddress(guard)}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleCopyAddress(guard)}
                      sx={{
                        color: colors.primary,
                        padding: '4px',
                        '&:hover': {
                          backgroundColor: `${colors.primary}20`,
                        },
                      }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
                <Divider sx={dividerStyles} />
              </>
            )}

            <Box>
              <Typography
                sx={{
                  ...detailRowLabelStyles,
                  mb: 1.5,
                }}
              >
                Owners ({owners.length}):
              </Typography>
              {owners.length > 0 ? (
                <List
                  sx={{
                    maxHeight: '300px',
                    overflowY: 'auto',
                    border: `1px solid ${colors.primary}33`,
                    borderRadius: 1,
                    p: 1,
                  }}
                >
                  {owners.map((owner, index) => (
                    <ListItem key={owner} sx={ownerItemStyles} disablePadding>
                      <Typography
                        sx={{
                          ...detailRowValueStyles,
                          fontFamily: 'monospace',
                          fontSize: isDesktop ? '0.95rem' : '0.85rem',
                          textAlign: 'left',
                        }}
                      >
                        {index + 1}. {isDesktop ? owner : formatAddress(owner)}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleCopyAddress(owner)}
                        sx={{
                          color: colors.primary,
                          padding: '4px',
                          '&:hover': {
                            backgroundColor: `${colors.primary}20`,
                          },
                        }}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography
                  sx={{
                    ...detailRowValueStyles,
                    textAlign: 'left',
                    fontStyle: 'italic',
                    opacity: 0.7,
                  }}
                >
                  No owners found
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 2,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            ...MODAL_BUTTON_STYLES,
            color: colors.primary,
            borderColor: colors.primary,
            '&:hover': {
              borderColor: colors.primary,
              backgroundColor: `${colors.primary}22`,
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}
